import React, { useRef, useState } from 'react';
import {
  Box,
  Checkbox,
  CircularProgress,
  Divider,
  IconButton,
  InputBase,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import { useSortable, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import AttachmentStrip from './AttachmentStrip';
import { ACCEPTED_IMAGE_TYPES, imagesFromClipboard } from '../../utils/attachmentsApi';

const MAX_IMAGES_PER_TASK = 5;

/**
 * One scratchpad task as a full-width row. It is a sortable item so it can be
 * reordered within the list and, because it shares the bet's DndContext, dragged
 * onto a scope card to graduate into that scope's todos. Only the drag handle
 * starts a drag, so the checkbox, inline edit, and delete stay clickable.
 */
const TaskRow = ({
  task,
  canEdit,
  onToggleDone,
  onEditTitle,
  onDelete,
  onAttach,
  onDeleteAttachment,
  uploading,
  deletingAttachmentId,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: task.id });
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(task.title);
  const fileInputRef = useRef(null);

  const attachments = task.attachments || [];
  const atLimit = attachments.length >= MAX_IMAGES_PER_TASK;

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 1 : undefined,
    opacity: isDragging ? 0.4 : undefined,
  };

  const pickFiles = (event) => {
    const files = Array.from(event.target.files || []);
    // Reset so picking the same file twice in a row still fires a change event.
    event.target.value = '';
    if (files.length) onAttach(task, files);
  };

  /**
   * Pasting a screenshot while editing the title attaches it instead of dropping
   * an image path into the text. An ordinary text paste is left alone, so the
   * field behaves normally the rest of the time.
   */
  const handlePaste = (event) => {
    const images = imagesFromClipboard(event);
    if (!images.length) return;
    event.preventDefault();
    onAttach(task, images);
  };

  const commitEdit = () => {
    const next = draft.trim();
    setEditing(false);
    if (next && next !== task.title) {
      onEditTitle(task, next);
    } else {
      setDraft(task.title);
    }
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        py: 0.25,
        borderRadius: 1,
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {canEdit && (
          <Tooltip title="Drag to reorder, or onto a scope to assign">
            <IconButton
              size="small"
              aria-label="Drag task"
              sx={{ cursor: 'grab', touchAction: 'none' }}
              {...attributes}
              {...listeners}
            >
              <DragIndicatorIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        <Checkbox
          size="small"
          checked={task.done}
          disabled={!canEdit}
          onChange={(e) => onToggleDone(task, e.target.checked)}
          sx={{ p: 0.5 }}
        />

        {editing ? (
          <InputBase
            value={draft}
            autoFocus
            fullWidth
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commitEdit}
            onPaste={canEdit && onAttach ? handlePaste : undefined}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); commitEdit(); }
              if (e.key === 'Escape') { setDraft(task.title); setEditing(false); }
            }}
            sx={{ flexGrow: 1, fontSize: '0.875rem' }}
          />
        ) : (
          <Typography
            variant="body2"
            onClick={canEdit ? () => { setDraft(task.title); setEditing(true); } : undefined}
            sx={{
              flexGrow: 1,
              cursor: canEdit ? 'text' : 'default',
              color: task.done ? 'text.disabled' : 'text.primary',
              textDecoration: task.done ? 'line-through' : 'none',
              wordBreak: 'break-word',
            }}
          >
            {task.title}
          </Typography>
        )}

        {canEdit && onAttach && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPTED_IMAGE_TYPES.join(',')}
              hidden
              onChange={pickFiles}
            />
            <Tooltip title={atLimit
              ? `A task can hold ${MAX_IMAGES_PER_TASK} images`
              : 'Attach an image, or paste one while editing the title'}>
              <span>
                <IconButton
                  size="small"
                  aria-label="Attach image"
                  disabled={atLimit || uploading}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {uploading
                    ? <CircularProgress size={16} />
                    : <ImageOutlinedIcon fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
          </>
        )}

        {canEdit && (
          <Tooltip title="Delete task">
            <IconButton size="small" aria-label="Delete task" onClick={() => onDelete(task)}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Indented to line up under the title rather than the drag handle. */}
      <Box sx={{ pl: canEdit ? 8.5 : 4.5 }}>
        <AttachmentStrip
          attachments={attachments}
          canEdit={canEdit && Boolean(onDeleteAttachment)}
          onDelete={(attachment) => onDeleteAttachment(task, attachment)}
          deletingId={deletingAttachmentId}
        />
      </Box>
    </Box>
  );
};

/**
 * The bet's scratchpad: a full-width band between the hill chart and the scopes
 * board where anyone on the team jots loose tasks. Dragging a task onto a scope
 * graduates it into that scope's todos; the rows can also be reordered. Sits
 * inside the bet's DndContext so a single drag can do either.
 */
const BetScratchpad = ({
  tasks = [],
  canEdit = true,
  onAdd,
  onToggleDone,
  onEditTitle,
  onDelete,
  onAttach,
  onDeleteAttachment,
  uploadingTaskId = null,
  deletingAttachmentId = null,
  attachmentError = null,
}) => {
  const [newTitle, setNewTitle] = useState('');
  const newFileInputRef = useRef(null);
  // Images pasted or picked before the task exists. The task has to be created
  // first to have something to attach them to, so they wait here until it is.
  const [pending, setPending] = useState([]);

  const submitNew = () => {
    const title = newTitle.trim();
    if (!title) return;
    onAdd(title, pending);
    setNewTitle('');
    setPending([]);
  };

  const handleNewPaste = (event) => {
    if (!onAttach) return;
    const images = imagesFromClipboard(event);
    if (!images.length) return;
    event.preventDefault();
    setPending(prev => [...prev, ...images].slice(0, MAX_IMAGES_PER_TASK));
    // A pasted screenshot is usually the whole point of the task, so give the
    // title a sensible default rather than blocking on an empty field.
    setNewTitle(prev => prev || 'Screenshot');
  };

  const pickNewFiles = (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (!files.length) return;
    setPending(prev => [...prev, ...files].slice(0, MAX_IMAGES_PER_TASK));
    setNewTitle(prev => prev || files[0].name.replace(/\.[^.]+$/, ''));
  };

  return (
    <Box sx={{ mt: 2, width: '100%' }}>
      <Divider sx={{ mb: 1.5 }} />

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: '.08em', color: 'text.secondary' }}>
          {`SCRATCHPAD · ${tasks.length}`}
        </Typography>
      </Box>

      <SortableContext items={tasks.map(task => task.id)} strategy={verticalListSortingStrategy}>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          {tasks.map(task => (
            <TaskRow
              key={task.id}
              task={task}
              canEdit={canEdit}
              onToggleDone={onToggleDone}
              onEditTitle={onEditTitle}
              onDelete={onDelete}
              onAttach={onAttach}
              onDeleteAttachment={onDeleteAttachment}
              uploading={uploadingTaskId === task.id}
              deletingAttachmentId={deletingAttachmentId}
            />
          ))}
        </Box>
      </SortableContext>

      {tasks.length === 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Nothing here yet. Jot a loose task, then drag it onto a scope to make it real.
        </Typography>
      )}

      {canEdit && (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
            <TextField
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              onPaste={onAttach ? handleNewPaste : undefined}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submitNew(); } }}
              placeholder={onAttach ? 'Add a task, or paste a screenshot…' : 'Add a task…'}
              size="small"
              variant="standard"
              fullWidth
            />

            {onAttach && (
              <>
                <input
                  ref={newFileInputRef}
                  type="file"
                  accept={ACCEPTED_IMAGE_TYPES.join(',')}
                  hidden
                  onChange={pickNewFiles}
                />
                <Tooltip title="Attach an image to the new task">
                  <span>
                    <IconButton
                      size="small"
                      aria-label="Attach image to new task"
                      disabled={pending.length >= MAX_IMAGES_PER_TASK}
                      onClick={() => newFileInputRef.current?.click()}
                    >
                      <ImageOutlinedIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </>
            )}

            <Tooltip title="Add task">
              <span>
                <IconButton size="small" aria-label="Add task" onClick={submitNew} disabled={!newTitle.trim()}>
                  <AddIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>

          {pending.length > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', pl: 0.5 }}>
              {pending.length === 1
                ? '1 image will be attached when you add the task.'
                : `${pending.length} images will be attached when you add the task.`}
            </Typography>
          )}
        </>
      )}

      {attachmentError && (
        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5, pl: 0.5 }}>
          {attachmentError}
        </Typography>
      )}
    </Box>
  );
};

export default BetScratchpad;
